import { useStore } from '../state/store';
import { controller } from '../conversation/controller';
import { ws } from '../ws/client';
import type { ConversationMode } from '@voicer/shared';

const STATE_LABEL: Record<string, string> = {
  idle: 'Idle',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking',
  candidate: 'Speech detected',
  ducked: 'Ducked, evaluating',
  paused: 'Paused, listening',
  classifying: 'Classifying interjection',
};

function shortModel(model: string): string {
  if (model.includes('flash-lite')) return 'flash-lite';
  if (model.includes('flash')) return 'flash';
  if (model.includes('chirp')) return 'chirp_3';
  const bare = model.replace(/^koboldcpp\//i, '');
  return bare.length > 18 ? `${bare.slice(0, 16)}…` : bare;
}

export function StatusBar() {
  const connected = useStore((s) => s.connected);
  const status = useStore((s) => s.status);
  const settings = useStore((s) => s.settings);
  const arbiterState = useStore((s) => s.arbiterState);
  const audio = useStore((s) => s.audio);
  const scheduler = useStore((s) => s.scheduler);
  const compiled = useStore((s) => s.compiled);

  const setMode = (mode: ConversationMode) => void ws.rpc('settings.update', { patch: { mode } });

  const ctxPct = compiled && compiled.maxContext ? Math.round((compiled.totalTokens / compiled.maxContext) * 100) : null;
  const providers = status?.providers;
  const anyVertex = settings.llmProvider === 'vertex' || settings.sttProvider === 'vertex' || settings.ttsProvider === 'vertex';
  const anyKobold = settings.llmProvider === 'kobold' || settings.sttProvider === 'kobold' || settings.ttsProvider === 'kobold';

  return (
    <div className="statusbar">
      <span className={`pill ${connected ? 'ok' : 'bad'}`} title="Proxy WebSocket">
        proxy {connected ? 'connected' : 'offline'}
      </span>
      {providers ? (
        <span
          className={`pill ${providers.llm.reachable && providers.stt.reachable && providers.tts.reachable ? 'ok' : 'warn'}`}
          title={[
            `LLM ${providers.llm.provider}:${providers.llm.model}${providers.llm.error ? ` (${providers.llm.error})` : ''}`,
            `STT ${providers.stt.provider}:${providers.stt.model}${providers.stt.error ? ` (${providers.stt.error})` : ''}`,
            `TTS ${providers.tts.provider}:${providers.tts.model}${providers.tts.error ? ` (${providers.tts.error})` : ''}`,
          ].join('\n')}
        >
          LLM:{providers.llm.provider === 'vertex' ? 'vertex' : 'kobold'} · STT:{shortModel(providers.stt.model)} · TTS:
          {providers.tts.provider === 'vertex' ? shortModel(providers.tts.model) : 'kobold'}
        </span>
      ) : (
        <span className={`pill ${status?.reachable ? 'ok' : 'bad'}`} title={status?.error ?? status?.url}>
          kobold {status?.reachable ? status.version ?? 'up' : 'unreachable'}
        </span>
      )}
      {anyKobold && (
        <span className={`pill ${status?.reachable && settings.llmProvider === 'kobold' ? 'ok' : status?.flags.llm ? 'ok' : 'dim'}`} title={status?.error ?? status?.url}>
          kobold {status?.version ?? (status?.reachable ? 'up' : '—')}
        </span>
      )}
      {anyVertex && (
        <span className={`pill ${providers?.llm.provider === 'vertex' ? (providers.llm.reachable ? 'ok' : 'bad') : 'dim'}`} title={settings.vertexProjectId || 'Vertex'}>
          vertex
        </span>
      )}
      {status && (
        <>
          {settings.llmProvider === 'kobold' && (
            <span className="pill dim" title="Loaded model">
              {status.model ?? 'model ?'}
            </span>
          )}
          {settings.llmProvider === 'kobold' && (
            <>
              <span className={`pill ${status.flags.transcribe ? 'ok' : 'warn'}`}>STT</span>
              <span className={`pill ${status.flags.tts ? 'ok' : 'warn'}`}>TTS</span>
              <span className={`pill ${status.flags.embeddings ? 'ok' : 'dim'}`}>EMB</span>
            </>
          )}
          {anyKobold && (
            <span className={`pill ${status.perf?.idle ? 'dim' : 'busy'}`}>
              {status.perf?.idle ? 'idle' : 'busy'}
              {scheduler && scheduler.queued.length ? ` +${scheduler.queued.length} queued` : ''}
              {scheduler?.running ? ` (${scheduler.running.kind})` : ''}
            </span>
          )}
        </>
      )}
      <span className={`pill state-${arbiterState}`}>{STATE_LABEL[arbiterState] ?? arbiterState}</span>
      {ctxPct !== null && (
        <span className="pill dim" title="Context usage of last compiled prompt">
          ctx {compiled!.totalTokens}/{compiled!.maxContext} ({ctxPct}%)
        </span>
      )}
      <span className="spacer" />
      <div className="meter" title={`mic RMS ${audio.micLevel.toFixed(3)} / VAD ${audio.vadProb.toFixed(2)}`}>
        <div className="meter-fill" style={{ width: `${Math.min(100, audio.micLevel * 400)}%` }} />
        <div className="meter-vad" style={{ left: `${Math.min(100, audio.vadProb * 100)}%` }} />
      </div>
      {audio.echoChronic && <span className="pill warn" title="Mic keeps picking up the assistant's voice">headset recommended</span>}
      <div className="modes">
        {(['duplex', 'ptt', 'text'] as ConversationMode[]).map((m) => (
          <button
            key={m}
            className={settings.mode === m ? 'mode active' : 'mode'}
            disabled={m === 'duplex'}
            title={m === 'duplex' ? 'Duplex is temporarily disabled' : undefined}
            onClick={() => setMode(m)}
          >
            {m === 'ptt' ? 'push-to-talk' : m}
          </button>
        ))}
      </div>
      {!audio.micReady && settings.mode !== 'text' && (
        <button className="primary" onClick={() => void controller.start()}>
          Enable microphone
        </button>
      )}
    </div>
  );
}
